/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';
import ShinyText from './ShinyText';
import { useEffect} from 'react';

// --- SVG Icons ---
const Icons = {
    Menu: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>,
    Dashboard: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>,
    Subjects: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>,
    Students: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    Resources: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>,
    Schools: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V7l8-4 8 4v14" /><path d="M17 21v-8H7v8" /><line x1="9" y1="9" x2="9" y2="9" /><line x1="9" y1="13" x2="9" y2="13" /><line x1="15" y1="9" x2="15" y2="9" /><line x1="15" y1="13" x2="15" y2="13" /></svg>,
    Developer: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>,
    Users: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
    Settings: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1m16.24-3.76l-4.24 4.24M7.76 16.24l-4.24 4.24m4.24-4.24l4.24-4.24m4.24 4.24l4.24 4.24"></path></svg>,
    Notes: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"></path><path d="M8 8h8"></path><path d="M8 12h8"></path><path d="M8 16h5"></path></svg>,
    Logo: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M9 15l2 2 4-4"></path></svg>
};

export default function Navbar({ onLogout }) {
    const username = localStorage.getItem('username') || 'User';
    const isAdmin = localStorage.getItem('is_admin') === 'true';
    const isDeveloper = localStorage.getItem('user_role') === 'developer';
    const location = useLocation();

    // --- NEW REACTIVE EXPIRY STATE ---
    // We start as 'false' and let the useEffect find the real value
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const checkExpiry = () => {
            const expiryDate = localStorage.getItem('expiry_date');
            if (expiryDate) {
                const expired = new Date(expiryDate) < new Date();
                setIsExpired(expired);
            }
        };

        checkExpiry(); // Run once on load
        
        // Re-check every 60 seconds (keeps it live)
        const interval = setInterval(checkExpiry, 60000);
        return () => clearInterval(interval);
    }, [location.pathname]); // Re-run whenever the user changes pages
    // ---------------------------------

    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogoutClick = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            onLogout();
        }
    };

    const toggleMenu = () => setMenuOpen(!menuOpen);
    const closeMenu = () => setMenuOpen(false);

    const NavItem = ({ to, icon: Icon, label }) => {
        const isActive = location.pathname.startsWith(to);
        return (
            <li>
                <Link to={to} className={`nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
                    <span className="nav-icon"><Icon /></span>
                    <span className="nav-text">{label}</span>
                </Link>
            </li>
        );
    };

    return (
        <>
            {/* Top Navigation Bar */}
            <div className="main_Nav">
                <div className="nav-left">
                    <button className="hamburger-btn" onClick={toggleMenu} aria-label="Toggle menu">
                        <span className={`hamburger-icon ${menuOpen ? 'open' : ''}`}>
                            <span></span><span></span><span></span>
                        </span>
                    </button>
                    <div className="brand-container">
                        <h2 className="navbar-brand">
                            <ShinyText text="Marksure"
                            speed={8}
                            delay={2}
                            color="#ffffff"
                            shineColor="#1c723a" />
                        </h2>
                    </div>
                </div>
                <div className="nav-right">
                    <div className="user-info">
                        <span className="username">
                            {username}
                            {isAdmin && <span className="badge admin">ADMIN</span>}
                            {isDeveloper && <span className="badge dev">DEV</span>}
                        </span>
                    </div>
                    <button onClick={handleLogoutClick} className="logout-btn">
                        Logout
                    </button>
                </div>
            </div>

            {/* Sidebar Navigation */}
            <div className={`left_sidebar ${menuOpen ? 'active' : ''}`}>
                <ul className="nav-list">
                    {/* 1. DEVELOPERS ALWAYS SEE EVERYTHING */}
                    {isDeveloper ? (
                        <>
                            <NavItem to="/developer" icon={Icons.Developer} label="Developer" />
                            <NavItem to="/schools" icon={Icons.Schools} label="Schools" />
                            <NavItem to="/resources" icon={Icons.Resources} label="My Resources" />
                        </>
                    ) : (
                        /* 2. NORMAL TEACHERS CHECK FOR EXPIRY */
                        isExpired ? (
                            /* --- LOCKED STATE --- */
                            /* Only show Dashboard (Payment) and Settings */
                            <>
                                <NavItem to="/dashboard" icon={Icons.Dashboard} label="Dashboard" />
                                <div className="nav-divider">
                                    <span className="divider-label" style={{ color: '#d32f2f', fontSize: '0.8rem' }}>Access Locked</span>
                                </div>
                                <NavItem to="/settings" icon={Icons.Settings} label="Settings" />
                            </>
                        ) : (
                            /* --- ACTIVE STATE --- */
                            /* Show Everything */
                            <>
                                <NavItem to="/dashboard" icon={Icons.Dashboard} label="Dashboard" />
                                <NavItem to="/subjects" icon={Icons.Subjects} label="Subjects" />
                                <NavItem to="/notes" icon={Icons.Notes} label="Notes & Reminders" />
                                <NavItem to="/settings" icon={Icons.Settings} label="Settings" />
                                <NavItem to="/resources" icon={Icons.Resources} label="My Resources" />
                                {isAdmin && (
                                    <div className="nav-divider">
                                        <span className="divider-label">Admin</span>
                                        <NavItem to="/users" icon={Icons.Users} label="Users Management" />
                                    </div>
                                )}
                            </>
                        )
                    )}
                </ul>

                <div className="sidebar-footer">
                    <p>© 2025 Marksure</p>
                    <p>Created by Hendrik Grobbelaar</p>
                </div>
            </div>

            {menuOpen && <div className="sidebar-overlay" onClick={closeMenu}></div>}
        </>
    );
}